import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { ModerationReport, ModerationStats, UserRiskProfile } from './moderationTypes';

export function useModeration() {
  const [reports, setReports] = useState<ModerationReport[]>([]);
  const [stats, setStats] = useState<ModerationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [filterType, setFilterType] = useState<string>('');
  const [filterSeverity, setFilterSeverity] = useState<string>('');

  // Review modal
  const [reviewReport, setReviewReport] = useState<ModerationReport | null>(null);
  const [reviewStatus, setReviewStatus] = useState('dismissed');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewAction, setReviewAction] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Risk profile
  const [riskProfile, setRiskProfile] = useState<UserRiskProfile | null>(null);
  const [riskUserId, setRiskUserId] = useState<string | null>(null);
  const [riskLoading, setRiskLoading] = useState(false);

  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotif = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), type === 'error' ? 30000 : 5000);
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_moderation_queue', {
        p_status: filterStatus || null,
        p_type: filterType || null,
        p_severity: filterSeverity || null,
        p_limit: 50,
        p_offset: 0,
      });
      if (error) throw error;
      setReports(data || []);
    } catch (err: any) {
      showNotif(`Failed to load reports: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType, filterSeverity, showNotif]);

  const fetchStats = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_moderation_stats');
      if (error) throw error;
      setStats(data);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [fetchReports, fetchStats]);

  const loadRiskProfile = async (userId: string) => {
    setRiskLoading(true);
    setRiskUserId(userId);
    try {
      const { data, error } = await supabase.rpc('admin_get_user_risk_profile', { p_user_id: userId });
      if (error) throw error;
      setRiskProfile(data);
    } catch (err: any) {
      showNotif(`Failed to load risk profile: ${err.message}`, 'error');
    } finally {
      setRiskLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewReport) return;
    setReviewing(true);
    try {
      const { error } = await supabase.rpc('admin_review_report', {
        p_report_id: reviewReport.id,
        p_status: reviewStatus,
        p_admin_notes: reviewNotes.trim() || null,
        p_action_taken: reviewAction.trim() || null,
      });
      if (error) throw error;
      showNotif(`Report ${reviewStatus === 'dismissed' ? 'dismissed' : 'actioned'}`, 'success');
      setReviewReport(null);
      setReviewNotes('');
      setReviewAction('');
      fetchReports();
      fetchStats();
    } catch (err: any) {
      showNotif(`Failed: ${err.message}`, 'error');
    } finally {
      setReviewing(false);
    }
  };

  const openReview = (report: ModerationReport) => {
    setReviewReport(report);
    setReviewStatus('dismissed');
    setReviewNotes('');
    setReviewAction('');
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString();
  const formatDateTime = (d: string) => new Date(d).toLocaleString();
  const timeAgo = (d: string) => {
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: 'var(--rp-text)',
  };

  return {
    reports, stats, loading, filterStatus, setFilterStatus, filterType, setFilterType,
    filterSeverity, setFilterSeverity, reviewReport, setReviewReport, reviewStatus,
    setReviewStatus, reviewNotes, setReviewNotes, reviewAction, setReviewAction,
    reviewing, riskProfile, riskUserId, riskLoading, notification, setNotification,
    fetchReports, fetchStats, loadRiskProfile, handleReview, openReview,
    formatDate, formatDateTime, timeAgo, inputStyle,
  };
}

export type ModerationModel = ReturnType<typeof useModeration>;
